import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  CurrentBrandScope,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import { Invoice } from '@principle-theorem/principle-core';
import { PatientPermissions } from '@principle-theorem/principle-core/features';
import {
  type IBrand,
  type IInvoice,
  InvoiceStatus,
  InvoiceType,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  Firestore,
  getDoc,
  HISTORY_DATE_FORMAT,
  snapshot,
  type Timestamp,
  toMoment,
  toTimestamp,
  type WithRef,
} from '@principle-theorem/shared';
import {
  BehaviorSubject,
  combineLatest,
  type Observable,
  of,
  ReplaySubject,
} from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  EditDueDateDialogComponent,
  type IEditDueDateDialogData,
  type IEditDueDateDialogResult,
} from './edit-due-date-dialog/edit-due-date-dialog.component';

@Component({
  selector: 'pr-invoice-header',
  templateUrl: './invoice-header.component.html',
  styleUrls: ['./invoice-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class InvoiceHeaderComponent {
  private _dialog = inject(MatDialog);
  private _snackBar = inject(MatSnackBar);
  private _brandScope = inject(CurrentBrandScope);
  private _organisation = inject(OrganisationService);
  invoice$ = new ReplaySubject<WithRef<IInvoice>>(1);
  editable$ = new BehaviorSubject<boolean>(false);
  isPaid$: Observable<boolean>;
  canEditDueDate$: Observable<boolean>;
  hasInvoiceAdminPermission$: Observable<boolean>;
  link$: Observable<string[]>;
  replacedInvoice$: Observable<WithRef<IInvoice>>;
  replacedInvoiceLink$: Observable<string[]>;
  cancelledDate$: Observable<Timestamp | undefined>;
  treatmentDate$: Observable<Timestamp | undefined>;
  treatmentDateOverride$ = new BehaviorSubject<Timestamp | undefined>(
    undefined
  );
  readonly dateFormat = HISTORY_DATE_FORMAT;
  readonly invoiceType = InvoiceType;

  @Input()
  set invoice(invoice: WithRef<IInvoice>) {
    if (invoice) {
      this.invoice$.next(invoice);
    }
  }

  @Input()
  set editable(value: boolean) {
    this.editable$.next(value);
  }

  @Input()
  set treatmentDateOverride(date: Timestamp) {
    if (date) {
      this.treatmentDateOverride$.next(date);
    }
  }

  constructor() {
    this.isPaid$ = this.invoice$.pipe(
      map((invoice: IInvoice) => invoice.status === InvoiceStatus.Paid)
    );
    const hasPermission$ = this._organisation.userHasPermission$(
      PatientPermissions.AccountInvoiceAdmin
    );
    this.hasInvoiceAdminPermission$ = hasPermission$;
    this.canEditDueDate$ = combineLatest([this.invoice$, this.editable$]).pipe(
      map(
        ([invoice, editable]) =>
          editable &&
          !invoice.deleted &&
          (invoice.status === InvoiceStatus.Draft ||
            invoice.status === InvoiceStatus.Issued)
      )
    );

    this.link$ = combineLatest([
      this._brandScope.doc$.pipe(filterUndefined()),
      this.invoice$,
    ]).pipe(map(([brand, invoice]) => this._getInvoiceLink(brand, invoice)));

    this.cancelledDate$ = this.invoice$.pipe(
      map((invoice) =>
        Invoice.isCancelledInvoice(invoice) ? invoice.cancelledAt : undefined
      )
    );

    this.replacedInvoice$ = this.invoice$.pipe(
      switchMap((invoice) =>
        Invoice.isReplacedInvoice(invoice)
          ? getDoc(invoice.cancellation.replacement)
          : of(undefined)
      ),
      filterUndefined()
    );

    this.replacedInvoiceLink$ = combineLatest([
      this._brandScope.doc$.pipe(filterUndefined()),
      this.replacedInvoice$,
    ]).pipe(map(([brand, invoice]) => this._getInvoiceLink(brand, invoice)));

    this.treatmentDate$ = combineLatest([
      this.invoice$,
      this.treatmentDateOverride$,
    ]).pipe(
      switchMap(([invoice, treatmentDate]) =>
        treatmentDate
          ? of(treatmentDate)
          : Invoice.getAssociatedAppointment$(invoice).pipe(
              map((appointment) => appointment?.event?.from)
            )
      )
    );
  }

  async openEditDueDateDialog(): Promise<void> {
    const invoice = await snapshot(this.invoice$);
    const dialogData: IEditDueDateDialogData = {
      invoice,
      currentDueDate: invoice.due ? toMoment(invoice.due) : undefined,
    };
    const dialogRef = this._dialog.open<
      EditDueDateDialogComponent,
      IEditDueDateDialogData,
      IEditDueDateDialogResult
    >(EditDueDateDialogComponent, { data: dialogData });

    const result = await snapshot(dialogRef.afterClosed());
    if (!result) {
      return;
    }
    try {
      if (result.dueDate) {
        await Firestore.patchDoc(invoice.ref, {
          due: toTimestamp(result.dueDate),
        });
      } else {
        await Firestore.runTransaction(async (transaction) => {
          const current = await Firestore.getDoc(invoice.ref, transaction);
          delete current.due;
          await Firestore.saveDoc(current, undefined, transaction);
        });
      }
      this._snackBar.open('Due date updated');
    } catch {
      this._snackBar.open('Failed to update due date');
    }
  }

  private _getInvoiceLink(
    brand: WithRef<IBrand>,
    invoice: WithRef<IInvoice>
  ): string[] {
    const patientRef = Invoice.patientRef(invoice);
    return [
      '/',
      brand.slug,
      'patients',
      patientRef.id,
      'account',
      'invoices',
      invoice.ref.id,
    ];
  }
}
